import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  Play,
  ArrowLeft,
  MapPin,
  CalendarDays,
  Check,
  ShieldCheck,
  Sparkles,
  Ticket,
} from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { Countdown } from "@/components/Countdown";
import { TicketCard } from "@/components/tickets/TicketCard";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { hasAccess } from "@/lib/access";
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function PremiumEventView({ event }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const unlocked = ownsEvent(event.id) || params.get("unlocked") === "1";
  const soldOut = event.status === "sold_out";

  const firstAvailable =
    !event.tickets.inPerson.soldOut && event.tickets.inPerson.available !== 0
      ? "inPerson"
      : "online";
  const [selected, setSelected] = useState(firstAvailable);

  const goCheckout = (type) => navigate(`/checkout/${event.id}?type=${type}`);

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        {unlocked && (
          <div
            data-testid="access-granted-banner"
            className="mb-6 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4"
          >
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">
              Access unlocked — your ticket is ready. Enjoy the event!
            </p>
          </div>
        )}

        {/* Trailer / hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative aspect-[21/9] overflow-hidden rounded-3xl bg-[#0A1526]"
        >
          <img
            src={event.trailer}
            alt={event.title}
            className={`h-full w-full object-cover ${unlocked ? "" : "scale-105 blur-[2px]"}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />

          <div className="absolute left-5 top-5 flex flex-wrap gap-2">
            <EventStatusBadge status="premium" />
            {event.status !== "premium" && <EventStatusBadge status={event.status} />}
          </div>

          {/* Center CTA */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md">
              {unlocked ? (
                <Play className="ml-1 h-9 w-9 fill-white" />
              ) : (
                <Lock className="h-8 w-8" />
              )}
            </span>
            <p className="mt-4 text-sm font-medium text-white/80">
              {unlocked ? "Your live experience is ready" : "Premium event · watch a 30s trailer"}
            </p>
          </div>

          {/* Bottom info */}
          <div className="absolute bottom-5 left-5 right-5">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-4xl">
              {event.title}
            </h1>
            {event.status === "starting_soon" && !unlocked && (
              <div className="mt-3">
                <Countdown target={event.startsAt} />
              </div>
            )}
          </div>
        </motion.div>

        {/* Meta */}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-muted-foreground">
          <span className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-brand" /> {fmtDate(event.startsAt)}
          </span>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-brand" /> {event.location.name}, {event.location.city}
          </span>
          <Link
            to={`/profile/${event.host.username}`}
            className="flex items-center gap-2 font-medium text-foreground"
          >
            <Avatar className="h-6 w-6">
              <AvatarImage src={event.host.avatar} alt={event.host.displayName} />
              <AvatarFallback>{event.host.displayName[0]}</AvatarFallback>
            </Avatar>
            {event.host.displayName}
          </Link>
        </div>

        {/* Description */}
        <div className="mt-8 max-w-3xl border-t border-border pt-8">
          <h2 className="mb-3 font-heading text-xl font-bold tracking-tight text-foreground">
            About this event
          </h2>
          <p className="leading-relaxed text-muted-foreground">{event.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {event.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary px-3 py-1 text-sm text-muted-foreground">
                #{t}
              </span>
            ))}
          </div>
        </div>

        {/* Access / tickets */}
        <div id="tickets" className="mt-12 border-t border-border pt-10">
          <div className="mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" />
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-foreground">
              {unlocked ? "Your access" : "Choose your access"}
            </h2>
          </div>

          {unlocked ? (
            <div className="flex flex-col items-start gap-4 rounded-3xl border border-border bg-secondary/40 p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-heading text-lg font-bold text-foreground">
                  You're all set for {event.title}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Access details would be emailed to you. This is a demo — no real ticket issued.
                </p>
              </div>
              <Button asChild className="rounded-full bg-brand px-7 py-6 text-base hover:bg-brand-hover">
                <Link to={`/live-access/${event.id}`}>
                  <Play className="mr-1 h-5 w-5 fill-white" /> Enter live experience
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2">
                <TicketCard
                  type="inPerson"
                  ticket={event.tickets.inPerson}
                  selected={selected === "inPerson"}
                  onSelect={setSelected}
                />
                <TicketCard
                  type="online"
                  ticket={event.tickets.online}
                  selected={selected === "online"}
                  onSelect={setSelected}
                />
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                <Button
                  data-testid="unlock-access-btn"
                  disabled={soldOut}
                  onClick={() => goCheckout(selected)}
                  className="rounded-full bg-brand px-10 py-7 text-base hover:bg-brand-hover disabled:opacity-60"
                >
                  {soldOut ? (
                    "Sold out — join waitlist"
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" /> Unlock Access
                    </>
                  )}
                </Button>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Ticket className="h-3.5 w-3.5" /> Secure demo checkout · no real payment taken
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default PremiumEventView;
